import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquareHeart, Star, X } from "lucide-react";
import { createFeedback, getMyFeedback } from "../services/feedbackApi";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import ToastContainer from "./ToastContainer";

type FeedbackNudgeState = {
  dismissedMilestones: number;
  shownMilestones: number[];
  hasSubmitted: boolean;
};

type CVCreatedDetail = {
  count: number;
};

const FEEDBACK_NUDGE_KEY_PREFIX = "resumex_feedback_nudge:";
const FEEDBACK_SUBMITTED_EVENT = "feedback:submitted";
const CV_CREATED_EVENT = "cv:created";
const PROMPT_MILESTONES = [1, 3];

const getDefaultState = (): FeedbackNudgeState => ({
  dismissedMilestones: 0,
  shownMilestones: [],
  hasSubmitted: false,
});

/** Routes where a floating "rate us" button would be noise. */
const LAUNCHER_HIDDEN_PATHS = ["/", "/login", "/register"];

export default function FeedbackPrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, removeToast, success, error: showError } = useToast();

  const [state, setState] = useState<FeedbackNudgeState>(getDefaultState);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024,
  );
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openedFromMilestone, setOpenedFromMilestone] = useState<number | null>(
    null,
  );
  const userId = user?.id ?? "";
  const storageKey = useMemo(
    () => `${FEEDBACK_NUDGE_KEY_PREFIX}${userId}`,
    [userId],
  );
  const canRender = Boolean(user && user.role !== "admin");
  const persistState = useCallback(
    (next: FeedbackNudgeState) => {
      setState(next);
      if (!userId) {
        return;
      }
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Ignore storage write failures
      }
    },
    [storageKey, userId],
  );

  const closePrompt = () => {
    const shouldCountAsDismiss =
      openedFromMilestone !== null &&
      !state.hasSubmitted &&
      state.dismissedMilestones < 2;

    if (shouldCountAsDismiss) {
      persistState({
        ...state,
        dismissedMilestones: Math.min(2, state.dismissedMilestones + 1),
      });
    }

    setIsOpen(false);
    setOpenedFromMilestone(null);
    setSelectedRating(null);
  };

  const openPrompt = (milestone: number | null) => {
    setOpenedFromMilestone(milestone);
    setIsOpen(true);
  };

  const handleSubmitRating = async () => {
    if (!selectedRating) {
      showError("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    try {
      await createFeedback({
        rating: selectedRating,
        pageUrl: window.location.href,
      });

      persistState({
        ...state,
        hasSubmitted: true,
      });
      window.dispatchEvent(new Event(FEEDBACK_SUBMITTED_EVENT));
      success("Thanks for rating us!");
      setIsOpen(false);
      setOpenedFromMilestone(null);
      setSelectedRating(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to submit rating");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!canRender || !userId) {
      return;
    }

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (!stored) {
        setState(getDefaultState());
        return;
      }

      const parsed = JSON.parse(stored) as Partial<FeedbackNudgeState>;
      setState({
        dismissedMilestones:
          typeof parsed.dismissedMilestones === "number"
            ? parsed.dismissedMilestones
            : 0,
        shownMilestones: Array.isArray(parsed.shownMilestones)
          ? parsed.shownMilestones.filter(
              (item): item is number => typeof item === "number",
            )
          : [],
        hasSubmitted: Boolean(parsed.hasSubmitted),
      });
    } catch {
      setState(getDefaultState());
    }
  }, [canRender, userId, storageKey]);

  // localStorage is per-browser, so on its own it would nag someone who
  // already rated from another device. One cheap check against the server
  // settles it; a failure just leaves the local answer in place.
  useEffect(() => {
    if (!canRender || !userId || state.hasSubmitted) {
      return;
    }

    let cancelled = false;
    getMyFeedback({ limit: 1 })
      .then((result) => {
        if (cancelled || result.pagination.total === 0) return;
        persistState({ ...state, hasSubmitted: true });
      })
      .catch(() => {
        // Keep the local state.
      });

    return () => {
      cancelled = true;
    };
    // Runs once per user; re-running on every state change would refetch
    // after each dismissal for no gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRender, userId]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!canRender) {
      return;
    }

    const onFeedbackSubmitted = () => {
      if (state.hasSubmitted) {
        return;
      }
      persistState({
        ...state,
        hasSubmitted: true,
      });
    };

    const onCvCreated = (event: Event) => {
      if (state.hasSubmitted) {
        return;
      }

      const customEvent = event as CustomEvent<CVCreatedDetail>;
      const createdCount = customEvent.detail?.count;
      if (!createdCount || !PROMPT_MILESTONES.includes(createdCount)) {
        return;
      }

      if (state.shownMilestones.includes(createdCount)) {
        return;
      }

      const nextState: FeedbackNudgeState = {
        ...state,
        shownMilestones: [...state.shownMilestones, createdCount],
      };
      persistState(nextState);

      if (nextState.dismissedMilestones < 2) {
        openPrompt(createdCount);
      }
    };

    window.addEventListener(CV_CREATED_EVENT, onCvCreated as EventListener);
    window.addEventListener(FEEDBACK_SUBMITTED_EVENT, onFeedbackSubmitted);

    return () => {
      window.removeEventListener(
        CV_CREATED_EVENT,
        onCvCreated as EventListener,
      );
      window.removeEventListener(FEEDBACK_SUBMITTED_EVENT, onFeedbackSubmitted);
    };
  }, [canRender, persistState, state]);

  if (!canRender) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  const shouldShowLauncher =
    !isOpen &&
    !state.hasSubmitted &&
    !LAUNCHER_HIDDEN_PATHS.includes(location.pathname);

  return createPortal(
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          data-tour="rating-popup"
        >
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-cyan-50 px-5 py-4">
              <h3 className="text-lg font-semibold text-emerald-900">
                Help us improve!
              </h3>
              <button
                onClick={closePrompt}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <p className="text-sm text-gray-600">
                How was your experience creating resumes on ResumeX?
              </p>

              <div
                className="flex items-center justify-between gap-2"
                data-tour="rating-stars"
              >
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() =>
                      setSelectedRating((prev) => (prev === star ? null : star))
                    }
                    className="rounded-full p-2 transition-transform hover:scale-110"
                    aria-label={`Rate ${star} star`}
                  >
                    <Star
                      className={`h-8 w-8 ${
                        selectedRating && star <= selectedRating
                          ? "fill-amber-400 text-amber-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleSubmitRating}
                  disabled={isSubmitting}
                  data-tour="rating-submit"
                  className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : "Submit Rating"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setOpenedFromMilestone(null);
                    navigate("/feedback");
                  }}
                  className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Open Full Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom-right launcher for anyone who hasn't rated yet. On narrow
          screens it sits above the builder's bottom toolbar / tab bar. */}
      {shouldShowLauncher && (
        <button
          type="button"
          onClick={() => openPrompt(null)}
          data-tour="rating-launcher"
          className={`group fixed z-[110] flex items-center justify-center rounded-full bg-amber-500 text-white shadow-xl transition-all duration-300 hover:bg-amber-600 hover:shadow-2xl ${
            isMobile
              ? "bottom-[calc(6.5rem+env(safe-area-inset-bottom))] right-4 h-12 w-12 p-0"
              : "bottom-8 right-8 h-14 px-4 feedback-launcher-attention"
          }`}
          title="Rate ResumeX"
          aria-label="Rate ResumeX"
        >
          {!isMobile && (
            <span className="feedback-launcher-halo" aria-hidden="true" />
          )}
          <MessageSquareHeart
            className={`shrink-0 ${isMobile ? "h-5 w-5" : "h-6 w-6 feedback-launcher-icon"}`}
          />
          {!isMobile && (
            <span className="overflow-hidden whitespace-nowrap text-sm font-bold transition-all duration-300 max-w-0 opacity-0 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:ml-3">
              Rate ResumeX
            </span>
          )}
        </button>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </>,
    document.body,
  );
}
